import { ref, computed, getCurrentInstance, onMounted } from "vue";
import axios from "axios";
import type { TournamentMatch, TournamentResult, TournamentRound, TournamentState, TournamentStanding, TournamentRoster } from "@interfaces/tournament_type";

export function useTournamentState(eventId: number) {
  // Get the current instance to access global properties (toast)
  const instance = getCurrentInstance();

  // State
  const tournamentState = ref<TournamentState | null>(null);
  const isLoading = ref(false);
  const error = ref<string | null>(null);
  const successMessage = ref<string | null>(null);
  const actionLoading = ref(false);
  const matchLoadingStates = ref<Record<number, boolean>>({});
  const draggedPlayer = ref<TournamentMatch["player1"] | null>(null);
  const draggedMatchId = ref<number | null>(null);
  const isUpdatingPairings = ref(false);
  const showPreviousRounds = ref<Record<number, boolean>>({});

  const showSuccessMessage = (message: string) => {
    successMessage.value = message;
    setTimeout(() => {
      successMessage.value = null;
    }, 3000);
  };

  const showToast = (message: string, type: 'success' | 'error' | 'warning' | 'info' = 'success') => {
    const createToast = instance?.appContext?.app?.config?.globalProperties?.$toast;
    if (createToast) {
      createToast(message, {
        type,
        position: 'top-center',
        timeout: 3000,
        showCloseButton: true,
      });
    }
  };

  const clearMessages = () => {
    error.value = null;
    successMessage.value = null;
  };

  const loadTournamentState = async () => {
    try {
      isLoading.value = true;
      clearMessages();

      const response = await axios.get(
        `/api/v1/events/tournament/state?event_id=${eventId}`
      );

      tournamentState.value = response.data;

      // Initialize scoring fields for incomplete matches
      if (tournamentState.value && tournamentState.value.matches) {
        tournamentState.value.matches.forEach((match: TournamentMatch) => {
          if (!match.completed) {
            match.player1_victory_points = 0;
            match.player2_victory_points = 0;
            match.player1_models_cost = 0;
            match.player2_models_cost = 0;
          }
        });
      }
    } catch (err: any) {
      showToast("Ошибка загрузки состояния турнира", 'error');
    } finally {
      isLoading.value = false;
    }
  };

  const startTournament = async () => {
    try {
      actionLoading.value = true;
      error.value = null;

      const response = await axios.post("/api/v1/events/tournament/start", {
        event_id: eventId,
      });
      const result: TournamentResult = response.data;

      if (result.success) {
        showSuccessMessage("Турнир успешно запущен!");
        // Update local state instead of reloading
        if (tournamentState.value) {
          tournamentState.value.current_round = 1;
          tournamentState.value.can_advance = false;
          tournamentState.value.is_finished = false;
          // Load matches for the new round
          await loadTournamentState();
        }
      } else {
        showToast(result.error || "Ошибка запуска турнира", 'error');
      }
    } catch (err: any) {
      console.error("Error starting tournament:", err);
      showToast(err.response?.data?.error || "Ошибка запуска турнира", 'error');
    } finally {
      actionLoading.value = false;
    }
  };

  const randomizeRound = async () => {
    try {
      actionLoading.value = true;
      error.value = null;

      const response = await axios.post("/api/v1/events/tournament/randomize", {
        event_id: eventId,
      });
      const result: TournamentResult = response.data;

      if (result.success) {
        showSuccessMessage(result.message || "Незавершенные матчи перемешаны!");
        // Only reload matches to get the new pairings
        const matchesResponse = await axios.get(
          `/api/v1/events/tournament/state?event_id=${eventId}`
        );
        if (tournamentState.value) {
          tournamentState.value.matches = matchesResponse.data.matches;
          // Update standings to reflect any changes
          tournamentState.value.standings = matchesResponse.data.standings;
        }
      } else {
        showToast(result.error || "Ошибка перемешивания раунда", 'error');
      }
    } catch (err: any) {
      console.error("Error randomizing round:", err);
      showToast(err.response?.data?.error || "Ошибка перемешивания раунда", 'error');
    } finally {
      actionLoading.value = false;
    }
  };

  const advanceToNextRound = async () => {
    if (!tournamentState.value) return;

    actionLoading.value = true;
    error.value = null;

    // Check if this is the last round - if so, call finish API instead of advance
    const isLastRound = tournamentState.value.current_round >= tournamentState.value.total_rounds;

    if (isLastRound) {
      // Tournament is finished - call finish API
      try {
        const response = await axios.post("/api/v1/events/tournament/finish", {
          event_id: eventId,
        });
        const result: TournamentResult = response.data;

        if (result.success) {
          showSuccessMessage("Турнир завершен! Показаны финальные результаты.");

          // Reload tournament state from server to get updated data
          const stateResponse = await axios.get(
            `/api/v1/events/tournament/state?event_id=${eventId}`
          );
          const newState = stateResponse.data;
          // Update all tournament data from server to ensure consistency
          tournamentState.value.matches = newState.matches;
          tournamentState.value.standings = newState.standings;
          tournamentState.value.can_advance = newState.can_advance;
          tournamentState.value.is_finished = newState.is_finished;
        } else {
          showToast(result.error || "Ошибка завершения турнира", 'error');
        }
      } catch (err: any) {
        console.error("Error finishing tournament:", err);
        const errorMessage = err.response?.data?.error || err.response?.data?.message;
        showToast(errorMessage || "Ошибка завершения турнира", 'error');
      } finally {
        actionLoading.value = false;
      }
      return;
    }

    try {
      const response = await axios.post("/api/v1/events/tournament/advance", {
        event_id: eventId,
      });
      const result: TournamentResult = response.data;

      if (result.success) {
        showSuccessMessage(
          `Переход к раунду ${
            tournamentState.value ? tournamentState.value.current_round + 1 : 1
          }!`
        );
        // Update local state for next round
        if (tournamentState.value) {
          tournamentState.value.current_round += 1;
          tournamentState.value.can_advance = false;
          // Load new matches and update standings
          const stateResponse = await axios.get(
            `/api/v1/events/tournament/state?event_id=${eventId}`
          );
          const newState = stateResponse.data;
          tournamentState.value.matches = newState.matches;
          tournamentState.value.standings = newState.standings;
          tournamentState.value.can_advance = newState.can_advance;
          tournamentState.value.is_finished = newState.is_finished;
        }
      } else {
        showToast(result.error || "Ошибка перехода к следующему раунду", 'error');
      }
    } catch (err: any) {
      console.error("Error advancing to next round:", err);
      const errorMessage = err.response?.data?.error || err.response?.data?.message;
      showToast(errorMessage || "Ошибка перехода к следующему раунду", 'error');
    } finally {
      actionLoading.value = false;
    }
  };

  const finishMatch = async (matchId: number) => {
    try {
      matchLoadingStates.value[matchId] = true;

      const match = tournamentState.value?.matches.find((m) => m.id === matchId);
      if (!match) {
        error.value = "Матч не найден";
        return;
      }

      // Determine winner based on victory points only (no models cost tiebreaker)
      const player1VictoryPoints = match.player1_victory_points || 0;
      const player2VictoryPoints = match.player2_victory_points || 0;
      const player1ModelsCost = match.player1_models_cost || 0;
      const player2ModelsCost = match.player2_models_cost || 0;

      let winnerId: number | null;
      let loserId: number | null;
      let winnerVictoryPoints: number;
      let loserVictoryPoints: number;
      let winnerModelsCost: number;
      let loserModelsCost: number;

      // Determine winner: highest victory points only
      // If victory points are equal, it's a tie (winner_id = null, loser_id = null)
      if (player1VictoryPoints > player2VictoryPoints) {
        winnerId = match.player1.id;
        loserId = match.player2.id;
        winnerVictoryPoints = player1VictoryPoints;
        loserVictoryPoints = player2VictoryPoints;
        winnerModelsCost = player1ModelsCost;
        loserModelsCost = player2ModelsCost;
      } else if (player2VictoryPoints > player1VictoryPoints) {
        winnerId = match.player2.id;
        loserId = match.player1.id;
        winnerVictoryPoints = player2VictoryPoints;
        loserVictoryPoints = player1VictoryPoints;
        winnerModelsCost = player2ModelsCost;
        loserModelsCost = player1ModelsCost;
      } else {
        // Tie in victory points - always a tie, regardless of models cost
        winnerId = null;
        loserId = null;
        winnerVictoryPoints = player1VictoryPoints;
        loserVictoryPoints = player2VictoryPoints;
        winnerModelsCost = player1ModelsCost;
        loserModelsCost = player2ModelsCost;
      }

      const response = await axios.post("/api/v1/events/tournament/set_result", {
        event_id: eventId,
        match_id: matchId,
        winner_id: winnerId,
        loser_id: loserId,
        winner_victory_points: winnerVictoryPoints,
        loser_victory_points: loserVictoryPoints,
        winner_models_cost: winnerModelsCost,
        loser_models_cost: loserModelsCost,
        is_tie: winnerId === null,
      });

      const result: TournamentResult = response.data;

      if (result.success) {
        // Update local state for the specific match
        if (tournamentState.value) {
          const match = tournamentState.value.matches.find((m) => m.id === matchId);
          if (match) {
            match.completed = true;
            if (winnerId !== null && loserId !== null) {
              match.winner = {
                id: winnerId,
                name: match.player1.id === winnerId ? match.player1.name : match.player2.name,
              };
              match.loser = {
                id: loserId,
                name: match.player1.id === loserId ? match.player1.name : match.player2.name,
              };
            } else {
              // Tie - no winner or loser
              match.winner = null;
              match.loser = null;
            }
            // Update scoring data
            match.player1_victory_points = player1VictoryPoints;
            match.player2_victory_points = player2VictoryPoints;
            match.player1_models_cost = player1ModelsCost;
            match.player2_models_cost = player2ModelsCost;
          }

          // Check if current round is completed and update can_advance
          const currentRoundMatches = tournamentState.value.matches.filter(
            (m: TournamentMatch) => m.round === tournamentState.value!.current_round
          );
          const allCompleted = currentRoundMatches.every((m: TournamentMatch) => m.completed);
          if (allCompleted && currentRoundMatches.length > 0) {
            tournamentState.value.can_advance = true;
            showSuccessMessage("Раунд завершен! Можно переходить к следующему.");
          }

          // Update standings locally immediately for instant UI update
          updateStandingsLocally();

          // Reload tournament state to get updated data from server
          const stateResponse = await axios.get(
            `/api/v1/events/tournament/state?event_id=${eventId}`
          );
          const newState = stateResponse.data;
          // Update all tournament data from server to ensure consistency
          tournamentState.value.matches = newState.matches;
          tournamentState.value.standings = newState.standings;
          tournamentState.value.can_advance = newState.can_advance;
          tournamentState.value.is_finished = newState.is_finished;
        }
      } else {
        showToast(result.error || "Ошибка сохранения результата", 'error');
      }
    } catch (err: any) {
      console.error("Error finishing match:", err);
      showToast(err.response?.data?.error || "Ошибка завершения матча", 'error');
    } finally {
      matchLoadingStates.value[matchId] = false;
    }
  };

  const matchesByRound = computed(() => {
    if (!tournamentState.value) return {};

    const rounds: Record<number, TournamentMatch[]> = {};
    tournamentState.value.matches.forEach((match) => {
      if (!rounds[match.round]) {
        rounds[match.round] = [];
      }
      rounds[match.round].push(match);
    });

    return rounds;
  });

  const currentRoundMatches = computed(() => {
    if (!tournamentState.value) return [];
    return matchesByRound.value[tournamentState.value.current_round] || [];
  });

  const isTournamentStarted = computed(() => {
    return tournamentState.value && tournamentState.value.current_round > 0;
  });

  const canStartTournament = computed(() => {
    const canStart = (
      !isTournamentStarted.value &&
      tournamentState.value &&
      tournamentState.value.approved_participants >= 2
    );

    return canStart;
  });

  const isTournamentFinished = computed(() => {
    return tournamentState.value?.is_finished || false;
  });

  const hasUncompletedMatches = computed(() => {
    if (!tournamentState.value?.matches) return false;
    return tournamentState.value.matches.some(match =>
      match.round === tournamentState.value!.current_round && !match.completed
    );
  });

  // Function to detect if a match is a tie
  const isMatchTie = (match: any) => {
    if (!match.completed) return false;

    // Check if winner_id is null (explicit tie)
    if (match.winner === null || match.winner_id === null) return true;

    const player1VictoryPoints = match.player1_victory_points || 0;
    const player2VictoryPoints = match.player2_victory_points || 0;

    // Check if both players have the same victory points (models cost is not used for tie detection)
    return player1VictoryPoints === player2VictoryPoints;
  };

  const getRoundInfo = (roundNumber: number): TournamentRound | null => {
    if (!tournamentState.value?.tournament_rounds) return null;
    // Convert roundNumber to number to handle string keys from v-for
    const round = typeof roundNumber === 'string' ? parseInt(roundNumber, 10) : roundNumber;
    return (
      tournamentState.value.tournament_rounds.find((r) => r.round_number === round) ||
      null
    );
  };

  const totalRoundsFromData = computed(() => {
    return (
      tournamentState.value?.tournament_rounds?.length ||
      tournamentState.value?.total_rounds ||
      0
    );
  });

  // Extract player rosters from matches for the leaderboard
  const playerRosters = computed(() => {
    if (!tournamentState.value?.matches) return {};

    const rosters: Record<number, any[]> = {}; // Changed type to any[] for simplicity

    tournamentState.value.matches.forEach((match) => {
      // Handle player1 rosters
      if (match.player1_rosters && match.player1_rosters.length > 0) {
        rosters[match.player1.id] = match.player1_rosters;
      } else if (match.player1_roster) {
        // Fallback to single roster
        rosters[match.player1.id] = [match.player1_roster];
      }

      // Handle player2 rosters
      if (match.player2_rosters && match.player2_rosters.length > 0) {
        rosters[match.player2.id] = match.player2_rosters;
      } else if (match.player2_roster) {
        // Fallback to single roster
        rosters[match.player2.id] = [match.player2_roster];
      }
    });

    return rosters;
  });

  // Helper function to update standings locally with new scoring system
  const updateStandingsLocally = () => {
    if (!tournamentState.value) return;

    const allParticipants = tournamentState.value.matches
      .flatMap((match) => [match.player1, match.player2])
      .filter((player, index, arr) => arr.findIndex((p) => p.id === player.id) === index);

    const victoryPoints: Record<number, number> = {};
    const modelsCost: Record<number, number> = {};
    const tournamentPoints: Record<number, number> = {};

    // Calculate victory points, models cost, and tournament points for each player
    tournamentState.value.matches.forEach((match) => {
      if (match.completed) {
        // Player 1
        victoryPoints[match.player1.id] =
          (victoryPoints[match.player1.id] || 0) + (match.player1_victory_points || 0);
        modelsCost[match.player1.id] =
          (modelsCost[match.player1.id] || 0) + (match.player1_models_cost || 0);

        // Player 2
        victoryPoints[match.player2.id] =
          (victoryPoints[match.player2.id] || 0) + (match.player2_victory_points || 0);
        modelsCost[match.player2.id] =
          (modelsCost[match.player2.id] || 0) + (match.player2_models_cost || 0);

        // Calculate match result points and bonus tournament points
        const player1VictoryPoints = match.player1_victory_points || 0;
        const player2VictoryPoints = match.player2_victory_points || 0;

        // Determine match result points (win=2, tie=1, loss=0)
        let player1MatchPoints = 0;
        let player2MatchPoints = 0;

        if (player1VictoryPoints > player2VictoryPoints) {
          // Player 1 wins
          player1MatchPoints = 2;
          player2MatchPoints = 0;
        } else if (player2VictoryPoints > player1VictoryPoints) {
          // Player 2 wins
          player1MatchPoints = 0;
          player2MatchPoints = 2;
        } else {
          // Tie in victory points - always a tie, regardless of models cost
          player1MatchPoints = 1;
          player2MatchPoints = 1;
        }

        // Add match result points to tournament points
        tournamentPoints[match.player1.id] = (tournamentPoints[match.player1.id] || 0) + player1MatchPoints;
        tournamentPoints[match.player2.id] = (tournamentPoints[match.player2.id] || 0) + player2MatchPoints;

        // Note: Bonus points from victory points are calculated once at the end, not per match
      }
    });

    // Add bonus points from victory points (max 1 per round, only if 5+ victory points in a match)
    allParticipants.forEach((player) => {
      // Count how many matches the player scored 5+ victory points
      let bonusMatches = 0;
      tournamentState.value.matches.forEach((match) => {
        if (match.completed) {
          const playerVictoryPoints = match.player1.id === player.id
            ? match.player1_victory_points
            : match.player2_victory_points;

          if (playerVictoryPoints >= 5) {
            bonusMatches++;
          }
        }
      });

      // Add 1 tournament point for each match with 5+ victory points
      tournamentPoints[player.id] += bonusMatches;
    });

    // Create standings, preserving hero_name from existing standings if available
    const existingStandingsMap = new Map(
      (tournamentState.value.standings || []).map((s) => [s.player.id, s])
    );

    const standings = allParticipants.map((player) => {
      const existingStanding = existingStandingsMap.get(player.id);
      return {
        player,
        victory_points: victoryPoints[player.id] || 0,
        models_cost: modelsCost[player.id] || 0,
        wins: tournamentPoints[player.id] || 0, // Use tournament points as "wins"
        hero_name: existingStanding?.hero_name, // Preserve hero_name if it exists
      };
    });

    // Sort by tournament points (descending), then by victory points (descending), then by models cost (descending), then by player ID
    standings.sort((a, b) => {
      if (a.wins !== b.wins) return b.wins - a.wins; // Tournament points
      if (a.victory_points !== b.victory_points) return b.victory_points - a.victory_points;
      if (a.models_cost !== b.models_cost) return b.models_cost - a.models_cost;
      return a.player.id - b.player.id;
    });

    tournamentState.value.standings = standings;
  };

  // Drag and drop methods
  const handlePlayerDragStart = (player: TournamentMatch["player1"], matchId: number) => {
    // Prevent dragging dummy players
    if (player.name === 'Dummy Player') {
      return;
    }
    draggedPlayer.value = player;
    draggedMatchId.value = matchId;
  };

  const handlePlayerDrop = async (
    targetPlayer: TournamentMatch["player1"],
    targetMatchId: number
  ) => {
    if (
      !draggedPlayer.value ||
      !draggedMatchId.value ||
      draggedMatchId.value === targetMatchId ||
      targetPlayer.name === 'Dummy Player' ||
      draggedPlayer.value.name === 'Dummy Player'
    ) {
      return;
    }

    try {
      // Find the source and target matches
      const sourceMatch = tournamentState.value?.matches.find(
        (m) => m.id === draggedMatchId.value
      );
      const targetMatch = tournamentState.value?.matches.find(
        (m) => m.id === targetMatchId
      );

      if (!sourceMatch || !targetMatch || sourceMatch.completed || targetMatch.completed) {
        return;
      }

      // Determine which player in the source match is being dragged
      const isDraggedPlayer1 = sourceMatch.player1.id === draggedPlayer.value.id;
      const draggedPlayerInSource = isDraggedPlayer1
        ? sourceMatch.player1
        : sourceMatch.player2;
      const otherPlayerInSource = isDraggedPlayer1
        ? sourceMatch.player2
        : sourceMatch.player1;

      // Determine which player in the target match is being replaced
      const isTargetPlayer1 = targetMatch.player1.id === targetPlayer.id;
      const targetPlayerInTarget = isTargetPlayer1
        ? targetMatch.player1
        : targetMatch.player2;
      const otherPlayerInTarget = isTargetPlayer1
        ? targetMatch.player2
        : targetMatch.player1;

      // Swap players
      if (isDraggedPlayer1) {
        sourceMatch.player1 = targetPlayerInTarget;
      } else {
        sourceMatch.player2 = targetPlayerInTarget;
      }

      if (isTargetPlayer1) {
        targetMatch.player1 = draggedPlayerInSource;
      } else {
        targetMatch.player2 = draggedPlayerInSource;
      }

      // Update the backend with the new pairings
      await updateMatchPairings();

      showSuccessMessage("Игроки успешно перемещены!");
    } catch (err: any) {
      console.error("Error updating match pairings:", err);
      showToast("Ошибка при перемещении игроков", 'error');
    } finally {
      draggedPlayer.value = null;
      draggedMatchId.value = null;
    }
  };

  const updateMatchPairings = async () => {
    if (!tournamentState.value) return;

    isUpdatingPairings.value = true;
    try {
      // Prepare the pairings data for the API
      const pairings = tournamentState.value.matches
        .filter((match: TournamentMatch) => !match.completed)
        .map((match: TournamentMatch) => ({
          match_id: match.id,
          player1_id: match.player1.id,
          player2_id: match.player2.id,
        }));

      const response = await axios.post("/api/v1/events/tournament/update_pairings", {
        event_id: eventId,
        pairings: pairings,
      });

      if (response.data.success) {
        showSuccessMessage("Пары игроков сохранены!");
      } else {
        showToast(response.data.error || "Ошибка сохранения пар", 'error');
      }
    } catch (err: any) {
      console.error("Error updating match pairings:", err);
      showToast(err.response?.data?.error || "Ошибка сохранения пар игроков", 'error');
    } finally {
      isUpdatingPairings.value = false;
    }
  };

  const toggleRoundVisibility = (round: number) => {
    // Convert round to number to handle string keys from v-for
    const roundNumber = typeof round === 'string' ? parseInt(round, 10) : round;
    showPreviousRounds.value[roundNumber] = !showPreviousRounds.value[roundNumber];
  };

  const getCompletedMatchesCount = (round: number) => {
    if (!tournamentState.value?.matches) return 0;
    // Convert round to number to handle string keys from v-for
    const roundNumber = typeof round === 'string' ? parseInt(round, 10) : round;
    return tournamentState.value.matches.filter(match => match.round === roundNumber && match.completed).length;
  };

  const isRoundActive = (round: number) => {
    // Convert round to number to handle string keys from v-for
    const roundNumber = typeof round === 'string' ? parseInt(round, 10) : round;
    const currentRound = tournamentState.value?.current_round;

    // If tournament is finished, no round is active
    if (tournamentState.value?.is_finished) {
      return false;
    }

    return roundNumber === currentRound;
  };

  onMounted(() => {
    loadTournamentState();

    // Listen for roster updates with specific match data
    window.addEventListener('tournament-roster-updated', (event: any) => {
      if (event.detail && event.detail.matchId && event.detail.playerId && event.detail.rosterIndex !== undefined) {
        // Update only the specific match's roster data
        updateMatchRosterLocally(event.detail.matchId, event.detail.playerId, event.detail.rosterIndex);
      } else {
        // Fallback to full reload if no specific data provided
        loadTournamentState();
      }
    });
  });

  // Function to update roster data locally without full reload
  const updateMatchRosterLocally = (matchId: number, playerId: number, rosterIndex: number) => {
    if (!tournamentState.value?.matches) return;

    const match = tournamentState.value.matches.find(m => m.id === matchId);
    if (!match) return;

    // Update the active roster index for the specific player
    if (match.player1.id === playerId) {
      match.player1_active_roster_index = rosterIndex;
    } else if (match.player2.id === playerId) {
      match.player2_active_roster_index = rosterIndex;
    }
  };

  const replayMatch = async (matchId: number) => {
    try {
      matchLoadingStates.value[matchId] = true;
      error.value = null;

      const match = tournamentState.value?.matches.find((m) => m.id === matchId);
      if (!match) {
        error.value = "Матч не найден";
        return;
      }

      const response = await axios.post("/api/v1/events/tournament/replay_match", {
        event_id: eventId,
        match_id: matchId,
      });
      const result: TournamentResult = response.data;

      if (result.success) {
        // Update local state for the specific match - clear winner, loser, and scores
        if (tournamentState.value) {
          const match = tournamentState.value.matches.find((m) => m.id === matchId);
          if (match) {
            match.winner = null;
            match.loser = null;
            match.completed = false;
            match.player1_victory_points = 0;
            match.player2_victory_points = 0;
            match.player1_models_cost = 0;
            match.player2_models_cost = 0;
          }

          // Check if current round is still completed and update can_advance
          const currentRound = tournamentState.value.current_round;
          const currentRoundMatches = tournamentState.value.matches.filter(
            (m) => m.round === currentRound
          );
          const allCompleted = currentRoundMatches.every((m) => m.completed);
          tournamentState.value.can_advance = allCompleted;

          // If tournament was finished, check if it should still be finished
          if (tournamentState.value.is_finished) {
            const totalRounds = tournamentState.value.total_rounds || 0;
            const allRoundsCompleted = currentRoundMatches.length > 0 &&
              currentRound >= totalRounds &&
              allCompleted;
            tournamentState.value.is_finished = allRoundsCompleted;
          }
        }

        showSuccessMessage(result.message || 'Матч разблокирован для редактирования');
      } else {
        showToast(result.error || "Ошибка разблокировки матча", 'error');
      }
    } catch (err: any) {
      console.error("Error replaying match:", err);
      showToast(err.response?.data?.error || "Ошибка разблокировки матча", 'error');
    } finally {
      matchLoadingStates.value[matchId] = false;
    }
  };

  const replayRound = async (roundNumber: number) => {
    try {
      actionLoading.value = true;
      error.value = null;

      const response = await axios.post("/api/v1/events/tournament/replay_round", {
        event_id: eventId,
        round_number: roundNumber,
      });
      const result: TournamentResult = response.data;

      if (result.success) {
        showSuccessMessage(result.message || `Раунд ${roundNumber} разблокирован для редактирования`);
        await loadTournamentState();
      } else {
        showToast(result.error || "Ошибка разблокировки раунда", 'error');
      }
    } catch (err: any) {
      console.error("Error replaying round:", err);
      showToast(err.response?.data?.error || "Ошибка разблокировки раунда", 'error');
    } finally {
      actionLoading.value = false;
    }
  };

  const replayTournament = async () => {
    try {
      actionLoading.value = true;
      error.value = null;

      const response = await axios.post("/api/v1/events/tournament/replay_tournament", {
        event_id: eventId,
      });
      const result: TournamentResult = response.data;

      if (result.success) {
        showSuccessMessage(result.message || "Турнир разблокирован для редактирования");
        await loadTournamentState();
      } else {
        showToast(result.error || "Ошибка разблокировки турнира", 'error');
      }
    } catch (err: any) {
      console.error("Error replaying tournament:", err);
      showToast(err.response?.data?.error || "Ошибка разблокировки турнира", 'error');
    } finally {
      actionLoading.value = false;
    }
  };

  return {
    // State
    tournamentState,
    isLoading,
    error,
    successMessage,
    actionLoading,
    matchLoadingStates,
    draggedPlayer,
    isUpdatingPairings,
    showPreviousRounds,

    // Actions
    startTournament,
    randomizeRound,
    advanceToNextRound,
    finishMatch,
    handlePlayerDragStart,
    handlePlayerDrop,
    toggleRoundVisibility,
    replayMatch,
    replayRound,
    replayTournament,

    // Helpers
    isMatchTie,
    getRoundInfo,
    getCompletedMatchesCount,
    isRoundActive,

    // Computed
    matchesByRound,
    isTournamentStarted,
    canStartTournament,
    isTournamentFinished,
    hasUncompletedMatches,
    totalRoundsFromData,
    playerRosters,
  };
}
